import { useEffect, useState } from "react";
import NTNavbar from "@/components/feature/NTNavbar";
import NTFooter from "@/components/feature/NTFooter";
import { Link } from "react-router-dom";
import {
  fetchCategoryNames,
  getVideoEmbedUrl,
  mergeCategoryOptions,
} from "@/lib/productHelpers";

type Product = {
  id: string;
  name: string;
  description: string;
  category?: string;
  categories?: string[];
  images?: string[];
  main_image?: string;
  video_url?: string;
  visible: boolean;
  featured: boolean;
};

const categories = [
  "Todos",
  "Alimentício",
  "Metalúrgico",
  "Têxtil",
  "Automobilístico",
  "Higiene Pessoal",
  "Automação",
];

const getProductCategories = (product: Product) => {
  const productCategories =
    product.categories?.filter(Boolean) ?? [];

  if (productCategories.length > 0) {
    return productCategories;
  }

  return product.category ? [product.category] : [];
};

const formatProductCategories = (product: Product) =>
  getProductCategories(product).join(", ");

function ProductCard({
  product,
}: {
  product: Product;
}) {
  const [currentIndex, setCurrentIndex] =
    useState(0);

  const [showDetails, setShowDetails] =
    useState(false);

  const allImages = (() => {
    const imgs =
      product.images?.filter(
        (img) => img.trim() !== ""
      ) ?? [];

    if (
      product.main_image &&
      !imgs.includes(product.main_image)
    ) {
      return [
        product.main_image,
        ...imgs,
      ];
    }

    if (product.main_image) {
      return [
        product.main_image,
        ...imgs.filter(
          (img) =>
            img !== product.main_image
        ),
      ];
    }

    return imgs.length > 0
      ? imgs
      : [
          "https://placehold.co/600x400",
        ];
  })();

  const total = allImages.length;
  const videoEmbedUrl = getVideoEmbedUrl(product.video_url);

  const prev = (
    e: React.MouseEvent
  ) => {
    e.preventDefault();

    setCurrentIndex(
      (i) => (i - 1 + total) % total
    );
  };

  const next = (
    e: React.MouseEvent
  ) => {
    e.preventDefault();

    setCurrentIndex(
      (i) => (i + 1) % total
    );
  };

  return (
    <>
      <div className="border border-gray-200 rounded-lg overflow-hidden hover:border-[#1B1F5E] transition-all group bg-white">
        <div className="relative w-full h-52 overflow-hidden bg-gray-50">
          <img
            src={allImages[currentIndex]}
            alt={`${product.name}`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />

          {total > 1 && (
            <>
              <button
                onClick={prev}
                className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-black/40 hover:bg-black/65 text-white rounded-full w-8 h-8 flex items-center justify-center"
              >
                <i className="ri-arrow-left-s-line"></i>
              </button>

              <button
                onClick={next}
                className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-black/40 hover:bg-black/65 text-white rounded-full w-8 h-8 flex items-center justify-center"
              >
                <i className="ri-arrow-right-s-line"></i>
              </button>

              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
                {allImages.map((_, i) => (
                  <button
                    key={i}
                    onClick={(e) => {
                      e.preventDefault();

                      setCurrentIndex(i);
                    }}
                    className={`rounded-full transition-all ${
                      i === currentIndex
                        ? "bg-white w-4 h-1.5"
                        : "bg-white/50 w-1.5 h-1.5"
                    }`}
                  />
                ))}
              </div>
            </>
          )}

          <span className="absolute top-3 left-3 bg-[#1B1F5E] text-white font-bold text-xs px-2.5 py-1 rounded">
            {formatProductCategories(product)}
          </span>
        </div>

        <div className="p-5">
          <h3 className="font-rajdhani font-bold text-[#1a1a1a] text-lg leading-snug mb-2">
            {product.name}
          </h3>

          <p className="text-[#666] text-sm leading-relaxed mb-5 line-clamp-2">
            {product.description}
          </p>

          <div className="flex items-center justify-between gap-3">
            <button
              onClick={() =>
                setShowDetails(true)
              }
              className="border border-[#1B1F5E] text-[#1B1F5E] hover:bg-[#1B1F5E] hover:text-white transition-colors px-4 py-2 rounded-lg text-xs font-bold"
            >
              Ver Detalhes
            </button>

            <Link
              to="/contato"
              className="text-xs text-white bg-[#1B1F5E] hover:bg-[#2F4F8F] px-4 py-2 rounded-lg transition-colors whitespace-nowrap font-semibold"
            >
              Orçamento
            </Link>
          </div>
        </div>
      </div>

      {showDetails && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-5">
          <div className="bg-white rounded-2xl w-full max-w-6xl max-h-[95vh] overflow-y-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="p-6 border-b lg:border-b-0 lg:border-r">
                {videoEmbedUrl ? (
                  <iframe
                    src={videoEmbedUrl}
                    title={`Vídeo do produto ${product.name}`}
                    className="w-full aspect-video min-h-[320px] rounded-2xl border bg-gray-100"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                ) : (
                  <img
                    src={
                      allImages[currentIndex]
                    }
                    className="w-full h-[450px] object-cover rounded-2xl border"
                    alt={product.name}
                  />
                )}

                {!videoEmbedUrl && allImages.length > 1 && (
                  <div className="grid grid-cols-4 gap-3 mt-4">
                    {allImages.map(
                      (img, i) => (
                        <button
                          key={i}
                          onClick={() =>
                            setCurrentIndex(
                              i
                            )
                          }
                          className={`border rounded-xl overflow-hidden ${
                            currentIndex ===
                            i
                              ? "border-[#1B1F5E] border-2"
                              : "border-gray-200"
                          }`}
                        >
                          <img
                            src={img}
                            className="w-full h-24 object-cover"
                          />
                        </button>
                      )
                    )}
                  </div>
                )}

                {videoEmbedUrl && allImages.length > 0 && (
                  <div className="grid grid-cols-4 gap-3 mt-4">
                    {allImages.map(
                      (img, i) => (
                        <button
                          key={i}
                          onClick={() =>
                            setCurrentIndex(
                              i
                            )
                          }
                          className={`border rounded-xl overflow-hidden ${
                            currentIndex ===
                            i
                              ? "border-[#1B1F5E] border-2"
                              : "border-gray-200"
                          }`}
                        >
                          <img
                            src={img}
                            className="w-full h-24 object-cover"
                            alt={`${product.name} ${i + 1}`}
                          />
                        </button>
                      )
                    )}
                  </div>
                )}
              </div>

              <div className="p-8 flex flex-col">
                <div className="flex items-start justify-between gap-4 mb-5">
                  <div>
                    <p className="text-sm font-bold text-[#3FA7D6] uppercase tracking-widest mb-2">
                      {formatProductCategories(product)}
                    </p>

                    <h2 className="font-rajdhani font-black text-4xl text-[#1a1a1a] leading-tight">
                      {product.name}
                    </h2>
                  </div>

                  <button
                    onClick={() =>
                      setShowDetails(false)
                    }
                    className="w-10 h-10 rounded-full border flex items-center justify-center hover:bg-gray-100"
                  >
                    <i className="ri-close-line text-xl"></i>
                  </button>
                </div>

                <div className="text-[#555] leading-relaxed text-base whitespace-pre-line mb-8">
                  {product.description}
                </div>

                <div className="mt-auto">
                  <Link
                    to="/contato"
                    className="w-full bg-[#1B1F5E] hover:bg-[#2F4F8F] text-white font-bold py-4 rounded-xl flex items-center justify-center gap-2 transition-colors"
                  >
                    Solicitar Orçamento
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function ProdutosPage() {
  const [products, setProducts] =
    useState<Product[]>([]);

  const [loading, setLoading] =
    useState(true);

  const [activeCategory, setActiveCategory] =
    useState("Todos");

  const [categoryOptions, setCategoryOptions] =
    useState(categories);

  const [search, setSearch] =
    useState("");

  useEffect(() => {
    Promise.all([
      fetch("/api/products.php").then((res) => res.json()),
      fetchCategoryNames(),
    ])
      .then(([data, savedCategories]) => {
        setProducts(data);
        setCategoryOptions(
          mergeCategoryOptions(
            [
              savedCategories,
              categories,
              data.flatMap((product: Product) => getProductCategories(product)),
            ],
            true
          )
        );

        setLoading(false);
      })
      .catch((err) => {
        console.error(err);

        setLoading(false);
      });
  }, []);

  const filtered = products.filter(
    (p: any) => {
      const matchCat =
        activeCategory === "Todos" ||
        getProductCategories(p).includes(activeCategory);

      const matchSearch =
        p.name
          .toLowerCase()
          .includes(
            search.toLowerCase()
          ) ||
        p.description
          ?.toLowerCase()
          .includes(
            search.toLowerCase()
          );

      return (
        matchCat &&
        matchSearch &&
        p.visible !== false
      );
    }
  );

  return (
    <div className="min-h-screen bg-white font-inter">
      <NTNavbar />

      <main className="pt-[88px]">
        <section className="relative h-[340px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=industrial%20products%20machinery%20showcase%20factory%20floor%20bright%20clean%20modern%20manufacturing%20equipment%20professional&width=1400&height=400&seq=ntprodhero&orientation=landscape"
            alt="Produtos New Tech Solution"
            className="w-full h-full object-cover object-top"
          />

          <div className="absolute inset-0 bg-gradient-to-r from-[#1a1a1a]/80 via-[#1a1a1a]/50 to-transparent"></div>

          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-6 w-full">
              <div className="flex items-center gap-2 mb-3">
                <Link
                  to="/"
                  className="text-white/60 text-sm hover:text-white transition-colors"
                >
                  Home
                </Link>

                <span className="text-white/40 text-sm">
                  /
                </span>

                <span className="text-[#3FA7D6] text-sm font-semibold">
                  Produtos
                </span>
              </div>

              <h1 className="font-rajdhani font-bold text-white text-4xl md:text-5xl">
                Nossos Produtos
              </h1>

              <p className="text-white/70 text-base mt-2">
                Máquinas e equipamentos
                industriais desenvolvidos sob
                medida.
              </p>
            </div>
          </div>
        </section>

        <section className="bg-[#f5f5f5] py-8 border-b border-gray-200 sticky top-[88px] z-30">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex flex-wrap gap-2">
                {categoryOptions.map((cat) => (
                  <button
                    key={cat}
                    onClick={() =>
                      setActiveCategory(
                        cat
                      )
                    }
                    className={`px-4 py-2 rounded-md text-sm font-semibold transition-all whitespace-nowrap ${
                      activeCategory ===
                      cat
                        ? "bg-[#1B1F5E] text-white"
                        : "bg-white border border-gray-200 text-[#555]"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-md px-4 py-2 w-full md:w-64">
                <i className="ri-search-line text-sm text-gray-400"></i>

                <input
                  type="text"
                  placeholder="Buscar produto..."
                  value={search}
                  onChange={(e) =>
                    setSearch(
                      e.target.value
                    )
                  }
                  className="text-sm outline-none flex-1 text-gray-600 placeholder-gray-400"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-6">
            {loading ? (
              <div className="text-center py-20">
                Carregando...
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gray-100 text-gray-400 mx-auto mb-4">
                  <i className="ri-search-line text-2xl"></i>
                </div>

                <p className="font-rajdhani font-bold text-[#1a1a1a] text-xl mb-2">
                  Nenhum produto encontrado
                </p>

                <p className="text-[#999] text-sm">
                  Tente outro termo ou
                  categoria.
                </p>
              </div>
            ) : (
              <>
                <p className="text-[#999] text-sm mb-6">
                  {filtered.length} produto
                  {filtered.length !== 1
                    ? "s"
                    : ""}{" "}
                  encontrado
                  {filtered.length !== 1
                    ? "s"
                    : ""}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filtered.map(
                    (product) => (
                      <ProductCard
                        key={product.id}
                        product={
                          product
                        }
                      />
                    )
                  )}
                </div>
              </>
            )}
          </div>
        </section>

        <section className="bg-[#f5c518] py-5">
          <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <p className="font-rajdhani font-black text-[#1a1a1a] text-xl md:text-2xl uppercase">
                NÃO ENCONTROU O QUE
                PRECISA?
              </p>

              <p className="text-[#1a1a1a]/70 text-sm mt-0.5">
                Desenvolvemos máquinas sob
                medida para qualquer
                necessidade industrial.
              </p>
            </div>

            <Link
              to="/contato"
              className="whitespace-nowrap bg-[#1a1a1a] hover:bg-[#333] text-white font-bold px-8 py-3 rounded-md transition-colors text-sm"
            >
              Solicitar Projeto Especial
            </Link>
          </div>
        </section>
      </main>

      <NTFooter />
    </div>
  );
}
